import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Cog } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import api from '../services/api';
import { toast } from 'sonner';

const AutoclaveDevicesPage = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [formData, setFormData] = useState({
    number: '',
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const res = await api.get('/autoclave/devices/all');
      setDevices(res.data);
    } catch (error) {
      toast.error('Chyba při načítání autoklávy');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (device = null) => {
    if (device) {
      setEditingDevice(device);
      setFormData({
        number: device.number,
        name: device.name,
        description: device.description || ''
      });
    } else {
      setEditingDevice(null);
      setFormData({ number: '', name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.number || !formData.name) {
      toast.error('Vyplňte číslo a název');
      return;
    }

    try {
      if (editingDevice) {
        await api.put(`/autoclave/devices/${editingDevice.id}`, formData);
        toast.success('Autokláv upraven');
      } else {
        await api.post('/autoclave/devices', formData);
        toast.success('Autokláv přidán');
      }
      setDialogOpen(false);
      fetchDevices();
    } catch (error) {
      toast.error('Chyba při ukládání');
    }
  };

  const handleDelete = async () => {
    if (!deviceToDelete) return;
    try {
      await api.delete(`/autoclave/devices/${deviceToDelete.id}`);
      toast.success('Autokláv smazán');
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
      fetchDevices();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Autoklávy</h1>
              <p className="text-sm text-slate-500">Správa číselníku autoklávy</p>
            </div>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-2" />
            Přidat autokláv
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12">Načítání...</div>
        ) : devices.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Cog className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Žádné autoklávy</h3>
            <p className="text-slate-500 mb-4">Začněte přidáním prvního autoklávu</p>
            <Button onClick={() => handleOpenDialog()} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="h-4 w-4 mr-2" />
              Přidat autokláv
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Číslo</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Název</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Popis</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Stav</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Akce</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {devices.map((device) => (
                  <tr key={device.id} className={`hover:bg-slate-50 ${!device.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{device.number}</td>
                    <td className="px-4 py-3 text-slate-700">{device.name}</td>
                    <td className="px-4 py-3 text-slate-500 text-sm">{device.description || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        device.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {device.is_active ? 'Aktivní' : 'Neaktivní'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(device)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setDeviceToDelete(device); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDevice ? 'Upravit autokláv' : 'Nový autokláv'}</DialogTitle>
            <DialogDescription>
              {editingDevice ? 'Upravte údaje autoklávu' : 'Vyplňte údaje nového autoklávu'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Číslo autoklávu *</Label>
              <Input
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                placeholder="např. A1"
              />
            </div>
            <div className="space-y-2">
              <Label>Název *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="např. Autokláv hlavní"
              />
            </div>
            <div className="space-y-2">
              <Label>Popis</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Volitelný popis..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Zrušit</Button>
            <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700">
              {editingDevice ? 'Uložit' : 'Vytvořit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat autokláv?</AlertDialogTitle>
            <AlertDialogDescription>
              Opravdu chcete smazat autokláv "{deviceToDelete?.name}"? Tato akce je nevratná.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default AutoclaveDevicesPage;
